import { config } from '../config';
import axios from 'axios';
import { OptionContract, OptionChainData, OptionsPrediction } from '../reducers/optionsSlice';
import {
    StrikeHighlight,
    buildPastPeriodStrikeHighlights,
    buildNextPeriodStrikeHighlights,
    buildNextPeriodStrikeHighlightsFromPastVolume,
    aggregatePastPeriodChains,
    getStockPriceOnDate,
} from './optionsStrikeLogic';

export type { StrikeHighlight };
export {
    buildNextPeriodStrikeHighlights,
    buildNextPeriodStrikeHighlightsFromPastVolume,
    aggregatePastPeriodChains,
};

export function processOptionChain(
    contracts: OptionContract[],
    symbol: string,
    expirationDate?: string
): OptionChainData {
    const filtered = expirationDate
        ? contracts.filter(c => c.expiration_date === expirationDate)
        : contracts;

    const calls = new Map<number, { strike: number; volume: number }>();
    const puts = new Map<number, { strike: number; volume: number }>();
    const expirationDates = Array.from(new Set(filtered.map(contract => contract.expiration_date))).sort();

    const getContractVolume = (contract: OptionContract) => {
        const rawVolume = contract.volume ?? contract.open_interest ?? 1;
        const volume = typeof rawVolume === 'string' ? parseFloat(rawVolume) : rawVolume;
        return Number.isFinite(volume) && volume > 0 ? volume : 1;
    };

    filtered.forEach(contract => {
        const strike = typeof contract.strike_price === 'string'
            ? parseFloat(contract.strike_price)
            : contract.strike_price;
        const volume = getContractVolume(contract);

        if (contract.type === 'call') {
            if (!calls.has(strike)) {
                calls.set(strike, { strike, volume: 0 });
            }
            calls.get(strike)!.volume += volume;
        } else if (contract.type === 'put') {
            if (!puts.has(strike)) {
                puts.set(strike, { strike, volume: 0 });
            }
            puts.get(strike)!.volume += volume;
        }
    });

    return {
        symbol,
        expiration_date: expirationDate || 'all dates',
        expiration_dates: expirationDates,
        calls: Array.from(calls.values()).sort((a, b) => a.strike - b.strike),
        puts: Array.from(puts.values()).sort((a, b) => a.strike - b.strike),
    };
}

export function generateOptionsPrediction(
    chain: OptionChainData,
    currentPrice: number
): OptionsPrediction {
    const totalCallVolume = chain.calls.reduce((sum, c) => sum + (c.volume || 0), 0);
    const totalPutVolume = chain.puts.reduce((sum, p) => sum + (p.volume || 0), 0);
    const volumeRatio = totalCallVolume > 0 ? totalPutVolume / totalCallVolume : 1;

    const callsAbovePrice = chain.calls.filter(c => c.strike > currentPrice).length;
    const putsAbovePrice = chain.puts.filter(p => p.strike > currentPrice).length;

    let prediction: 'bullish' | 'bearish' | 'neutral' = 'neutral';
    const reasoning: string[] = [];
    let confidence = 50;

    if (volumeRatio < 0.8) {
        prediction = 'bullish';
        confidence = Math.min(90, 50 + (10 - volumeRatio * 10));
        reasoning.push(`Put/Call ratio is ${volumeRatio.toFixed(2)} (< 0.8 suggests bullish sentiment)`);
    } else if (volumeRatio > 1.2) {
        prediction = 'bearish';
        confidence = Math.min(90, 50 + (volumeRatio * 10 - 10));
        reasoning.push(`Put/Call ratio is ${volumeRatio.toFixed(2)} (> 1.2 suggests bearish sentiment)`);
    } else {
        reasoning.push(`Put/Call ratio is neutral at ${volumeRatio.toFixed(2)}`);
    }

    if (callsAbovePrice > chain.calls.length * 0.6) {
        if (prediction !== 'bearish') {
            prediction = 'bullish';
            confidence = Math.min(confidence + 10, 95);
        }
        reasoning.push(`${((callsAbovePrice / chain.calls.length) * 100).toFixed(0)}% of calls are above current price (bullish)`);
    }

    if (putsAbovePrice < chain.puts.length * 0.4) {
        if (prediction !== 'bullish') {
            prediction = 'bearish';
            confidence = Math.min(confidence + 10, 95);
        }
        reasoning.push(`Only ${((putsAbovePrice / chain.puts.length) * 100).toFixed(0)}% of puts are above current price (bearish)`);
    }

    return {
        symbol: chain.symbol,
        prediction,
        callVolume: totalCallVolume,
        putVolume: totalPutVolume,
        volumeRatio: parseFloat(volumeRatio.toFixed(2)),
        confidence: Math.round(confidence),
        reasoning,
    };
}

export interface PastExpirationAnalysis {
    expirationDate: string;
    startDate: string;
    endDate: string;
    chainData: OptionChainData;
    startPrice: number;
    endPrice: number;
    priceChangePercent: number;
    strikeHighlights: Record<number, StrikeHighlight>;
}

const getStrike = (contract: OptionContract): number => {
    return typeof contract.strike_price === 'string'
        ? parseFloat(contract.strike_price)
        : contract.strike_price;
};

interface OptionHistoricalBar {
    t: string;
    v: number;
}

const alpacaHeaders = {
    'APCA-API-KEY-ID': config.alpaca.keyId,
    'APCA-API-SECRET-KEY': config.alpaca.secretKey,
    'accept': 'application/json',
};

const fetchOptionBarsBatch = async (
    symbols: string[],
    start: string,
    end: string
): Promise<Record<string, OptionHistoricalBar[]>> => {
    if (!symbols.length) {
        return {};
    }

    const barsBySymbol: Record<string, OptionHistoricalBar[]> = {};
    const batchSize = 100;

    for (let i = 0; i < symbols.length; i += batchSize) {
        const batch = symbols.slice(i, i + batchSize);
        let pageToken: string | undefined;

        do {
            const query = new URLSearchParams({
                symbols: batch.join(','),
                timeframe: '1Day',
                start,
                end,
                limit: '10000',
                sort: 'asc',
            });

            if (pageToken) {
                query.set('page_token', pageToken);
            }

            const response = await axios({
                baseURL: config.alpaca.dataUrl,
                method: 'GET',
                url: `v1beta1/options/bars?${query.toString()}`,
                headers: alpacaHeaders,
            });

            const bars = response.data?.bars || {};
            Object.entries(bars).forEach(([symbol, symbolBars]) => {
                if (!Array.isArray(symbolBars)) {
                    return;
                }

                if (!barsBySymbol[symbol]) {
                    barsBySymbol[symbol] = [];
                }

                barsBySymbol[symbol].push(...symbolBars.map((bar: OptionHistoricalBar) => ({
                    t: bar.t,
                    v: bar.v,
                })));
            });

            pageToken = response.data?.next_page_token || undefined;
        } while (pageToken);
    }

    return barsBySymbol;
};

const selectContractsForWeek = (
    contracts: OptionContract[],
    stockPrice: number,
    weekEndDate: string,
    maxPerSide = 12
): OptionContract[] => {
    const validContracts = contracts.filter((contract) => contract.expiration_date >= weekEndDate);
    const calls = validContracts
        .filter((contract) => contract.type === 'call')
        .sort((a, b) => Math.abs(getStrike(a) - stockPrice) - Math.abs(getStrike(b) - stockPrice))
        .slice(0, maxPerSide);
    const puts = validContracts
        .filter((contract) => contract.type === 'put')
        .sort((a, b) => Math.abs(getStrike(a) - stockPrice) - Math.abs(getStrike(b) - stockPrice))
        .slice(0, maxPerSide);

    return [...calls, ...puts];
};

const sumWeeklyVolume = (
    bars: OptionHistoricalBar[],
    weekStart: string,
    weekEnd: string
): number => {
    const startTime = new Date(`${weekStart}T00:00:00Z`).getTime();
    const endTime = new Date(`${weekEnd}T23:59:59Z`).getTime();

    return bars.reduce((total, bar) => {
        const barTime = new Date(bar.t).getTime();
        if (barTime >= startTime && barTime <= endTime) {
            return total + bar.v;
        }
        return total;
    }, 0);
};

const buildWeeklyChainFromBars = (
    symbol: string,
    selectedContracts: OptionContract[],
    barsBySymbol: Record<string, OptionHistoricalBar[]>,
    weekStart: string,
    weekEnd: string
): OptionChainData => {
    const callVolumes = new Map<number, number>();
    const putVolumes = new Map<number, number>();

    selectedContracts.forEach((contract) => {
        const volume = sumWeeklyVolume(barsBySymbol[contract.symbol] || [], weekStart, weekEnd);
        if (volume <= 0) {
            return;
        }

        const strike = getStrike(contract);
        const volumes = contract.type === 'call' ? callVolumes : putVolumes;
        volumes.set(strike, (volumes.get(strike) || 0) + volume);
    });

    const calls = Array.from(callVolumes.entries())
        .map(([strike, volume]) => ({ strike, volume }))
        .sort((a, b) => a.strike - b.strike);
    const puts = Array.from(putVolumes.entries())
        .map(([strike, volume]) => ({ strike, volume }))
        .sort((a, b) => a.strike - b.strike);

    return {
        symbol,
        expiration_date: `${weekStart} to ${weekEnd}`,
        calls,
        puts,
    };
};

const buildPastExpirationAnalyses = async (
    symbol: string,
    stockBars: Array<{ t: string; c: number }>,
    contracts: OptionContract[]
): Promise<PastExpirationAnalysis[]> => {
    if (!stockBars.length || !contracts.length) {
        return [];
    }

    const periodStart = stockBars[0].t.split('T')[0];
    const periodEnd = stockBars[stockBars.length - 1].t.split('T')[0];
    const periodStartPrice = stockBars[0].c;
    const expirationDates = Array.from(
        new Set(
            contracts
                .map((contract) => contract.expiration_date)
                .filter((date) => date >= periodStart && date <= periodEnd)
        )
    ).sort();

    const analyses: PastExpirationAnalysis[] = [];

    for (const expirationDate of expirationDates) {
        const expiryContracts = contracts.filter((contract) => contract.expiration_date === expirationDate);
        const stockPriceAtExpiry = getStockPriceOnDate(stockBars, expirationDate);
        const selectedContracts = selectContractsForWeek(expiryContracts, periodStartPrice, expirationDate, 20);

        if (!selectedContracts.length || stockPriceAtExpiry <= 0) {
            continue;
        }

        try {
            const barsBySymbol = await fetchOptionBarsBatch(
                selectedContracts.map((contract) => contract.symbol),
                periodStart,
                expirationDate
            );
            const chain = buildWeeklyChainFromBars(symbol, selectedContracts, barsBySymbol, periodStart, expirationDate);
            chain.expiration_date = expirationDate;
            const totalVolume = chain.calls.reduce((sum, call) => sum + call.volume, 0)
                + chain.puts.reduce((sum, put) => sum + put.volume, 0);

            if (totalVolume <= 0) {
                continue;
            }

            const priceChangePercent = periodStartPrice > 0
                ? ((stockPriceAtExpiry - periodStartPrice) / periodStartPrice) * 100
                : 0;

            analyses.push({
                expirationDate,
                startDate: periodStart,
                endDate: expirationDate,
                chainData: chain,
                startPrice: periodStartPrice,
                endPrice: stockPriceAtExpiry,
                priceChangePercent,
                strikeHighlights: buildPastPeriodStrikeHighlights(chain, stockPriceAtExpiry),
            });
        } catch (error) {
            console.error(`Error building past expiration analysis for ${expirationDate}:`, error);
        }
    }

    return analyses;
};

export async function buildHistoricalOptionsStrategyHistory(
    symbol: string,
    stockBars: Array<{ t: string; c: number }>,
    contracts: OptionContract[]
): Promise<PastExpirationAnalysis[]> {
    if (stockBars.length < 2 || !contracts.length) {
        return [];
    }

    return buildPastExpirationAnalyses(symbol, stockBars, contracts);
};
